import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

interface MeterData {
  id: string;
  consumption: number;
  taxCut: number | null;
  isEligible: boolean;
}

@Component({
  selector: 'app-output-meters',
  standalone:true,
  imports:[CommonModule,FormsModule],
  templateUrl: './output-meters.component.html',
  styleUrls: ['./output-meters.component.scss']
})
export class OutputMetersComponent implements OnInit {

  meters: MeterData[] = [];

  filteredMeters: MeterData[] = [];

  searchTerm: string = '';
  
  isLoading: boolean = true;

  constructor(private router: Router) {

    const nav = this.router.getCurrentNavigation();
    if (nav?.extras.state && 'meters' in nav.extras.state) {
      const meterData = nav.extras.state['meters'] as Record<string, string>;
      this.processMeterData(meterData);
    }
  }

  ngOnInit(): void {

    if (window.history.state && window.history.state.meters) {
      const meterData = window.history.state.meters as Record<string, string>;
      console.log('ngOnInit - Got meter data from history:', meterData);
      
      if (this.meters.length === 0) {
        this.processMeterData(meterData);
      }
    }

    this.filteredMeters = [...this.meters];

    // if (this.meters.length === 0) {
    //   console.warn('No meter data found, loading demo data');
    //   this.loadDemoData();
    // }

    setTimeout(() => {
      this.isLoading = false;
    }, 800);
  }

  processMeterData(meterData: Record<string, string>): void {

    Object.values(meterData).forEach(meterId => {

      const consumption = Math.round((1000 + Math.random() * 4000) * 100) / 100;

      const isEligible = consumption < 3000;

      const taxCut = isEligible ? Math.round(consumption * 0.05 * 100) / 100 : null;
      
      this.meters.push({
        id: meterId,
        consumption,
        taxCut,
        isEligible
      });
    });
    
    console.log('Processed meters:', this.meters);
  }

  // Fallback demo data
  // loadDemoData(): void {
  //   this.meters = [
  //     { id: '12345678901', consumption: 2450.75, taxCut: 122.54, isEligible: true },
  //     { id: '23456789012', consumption: 1875.20, taxCut: 93.76, isEligible: true },
  //     { id: '34567890123', consumption: 3210.10, taxCut: null, isEligible: false }
  //   ];
  //   this.filteredMeters = [...this.meters];
  // }

  filterMeters(): void {
    if (!this.searchTerm || !this.searchTerm.trim()) {
      this.filteredMeters = [...this.meters];
      return;
    }
    
    const term = this.searchTerm.toLowerCase().trim();
    this.filteredMeters = this.meters.filter(meter => 
      meter.id.toLowerCase().includes(term)
    );
  }

  getEligibleCount(): number {
    return this.meters.filter(meter => meter.isEligible).length;
  }

  getTotalTaxCuts(): number {
    return this.meters
      .filter(meter => meter.isEligible && meter.taxCut !== null)
      .reduce((total, meter) => total + (meter.taxCut || 0), 0);
  }

  goToHome(): void {
    this.router.navigate(['/input-meters']);
  }

exportPDF(): void {

  const totalTaxCut = this.getTotalTaxCuts().toFixed(2);
  const eligibleCount = this.getEligibleCount();

  const tableRows = this.meters.map(meter => `
    <tr>
      <td style="padding: 8px; border: 1px solid #ddd;">${meter.id}</td>
      <td style="padding: 8px; border: 1px solid #ddd;">${meter.consumption.toFixed(2)}</td>
      <td style="padding: 8px; border: 1px solid #ddd;">${meter.taxCut ? '€' + meter.taxCut.toFixed(2) : '-'}</td>
      <td style="padding: 8px; border: 1px solid #ddd;">${meter.isEligible ? 'Yes' : 'No'}</td>
    </tr>
  `).join('');
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Meter Subsidy Report</title>
      <style>
        body { font-family: Arial, sans-serif; }
        h1 { color: #0f2554; }
        table { border-collapse: collapse; width: 100%; }
        th { background-color: #0f2554; color: white; padding: 10px; text-align: left; }
        .summary { margin: 20px 0; padding: 15px; background-color: #f9fafb; border-radius: 5px; }
        .highlight { color: #10b981; font-weight: bold; }
      </style>
    </head>
    <body>
      <h1>Meter Subsidy Eligibility Report</h1>
      
      <div class="summary">
        <p><strong>Total Eligible Meters:</strong> ${eligibleCount}</p>
        <p><strong>Total Subsidies:</strong> <span class="highlight">€${totalTaxCut}</span></p>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>Meter ID</th>
            <th>Consumption (kWh)</th>
            <th>Subsidy</th>
            <th>Eligible</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    </body>
    </html>
  `;

  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = 'meter_subsidies_report.html';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

}

exportCSV(): void {

  const totalTaxCut = this.getTotalTaxCuts().toFixed(2);

  const headers = ['Meter ID', 'Consumption (kWh)', 'Subsidy (€)', 'Eligible'];
  const rows = this.meters.map(meter => [
    meter.id,
    meter.consumption.toFixed(2),
    meter.taxCut ? meter.taxCut.toFixed(2) : '0.00',
    meter.isEligible ? 'Yes' : 'No'
  ]);

  rows.push(['', '', '', '']);
  rows.push(['TOTAL SUBSIDY:', '' + totalTaxCut, '']);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', 'meter_subsidies.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
}